package fyi.moko.hotpl.ui.placeform

import androidx.compose.runtime.Composable
import fyi.moko.hotpl.domain.model.GeoPoint
import fyi.moko.hotpl.domain.model.PhotoEvidence

/**
 * A picked photo: the downscaled JPEG bytes, plus **what its EXIF said before the
 * re-encode threw the tags away**.
 *
 * 태그를 파일에 남기지 않는 정책은 그대로다 — 사진의 GPS는 Storage로 올라가지 않는다. 다만
 * 고르는 순간에는 읽을 수 있고, 그 두 값이 "여기 가봤는가"에 가장 정확히 답한다
 * ([fyi.moko.hotpl.domain.model.inferVisit]). 읽어서 판단에만 쓰고 버린다.
 */
data class PickedImage(
    val bytes: ByteArray,
    val capturedAt: Long? = null,
    val location: GeoPoint? = null,
) {
    override fun equals(other: Any?): Boolean =
        this === other || (
            other is PickedImage &&
                bytes.contentEquals(other.bytes) &&
                capturedAt == other.capturedAt &&
                location == other.location
            )

    override fun hashCode(): Int =
        (31 * bytes.contentHashCode() + (capturedAt?.hashCode() ?: 0)) * 31 + (location?.hashCode() ?: 0)
}

/** The EXIF facts of this photo, for the visit inference. */
fun PickedImage.evidence(): PhotoEvidence = PhotoEvidence(capturedAt = capturedAt, location = location)

/**
 * Picks up to [MAX_PHOTOS_PER_PICK] images at once. Each one comes back downscaled and
 * re-encoded exactly like [ImagePicker] does, so the privacy properties are the same
 * (EXIF, including GPS, is dropped from the stored bytes). An empty list means the user
 * cancelled.
 */
fun interface MultiImagePicker {
    fun pick(onResult: (List<PickedImage>) -> Unit)
}

/** Creates a [MultiImagePicker] bound to the current composition. */
@Composable
expect fun rememberMultiImagePicker(): MultiImagePicker

/**
 * 한 번에 고를 수 있는 사진 수. 방문 한 번에 간판·내부·메뉴판·음식 몇 장이면 충분하고, 위로
 * 열어 두면 분류·저장이 길어지는 만큼 사용자가 기다린다.
 *
 * Android의 `PickMultipleVisualMedia`는 이 값을 계약을 만들 때 정해야 해서 실행 중에 못 바꾼다.
 */
const val MAX_PHOTOS_PER_PICK = 6

/** Longest-edge cap and JPEG quality applied when importing a picked photo. */
internal const val MAX_IMAGE_DIMENSION = 1080
internal const val JPEG_QUALITY = 80

/**
 * Power-of-two subsampling factor for decoding a photo of [width]×[height] down
 * toward [maxDimension]: the largest factor that keeps the longest edge at or above
 * the target, so the exact downscale that follows never upscales. Lets the decoder
 * skip pixels instead of materialising the full-resolution bitmap first.
 */
internal fun imageDecodeSampleSize(width: Int, height: Int, maxDimension: Int): Int {
    val longest = maxOf(width, height)
    if (longest <= 0) return 1
    var sampleSize = 1
    while (longest / (sampleSize * 2) >= maxDimension) {
        sampleSize *= 2
    }
    return sampleSize
}
